/**
 * Plain-language status labels shared by every screen (AB#6872, "Status labels").
 * Each domain value maps to one label, one tone and one icon; StatusChip renders it.
 *
 * PURE module: no React, MUI, axios or auth imports, so it is unit-testable and safe
 * to use from any helper.
 */
import type { StatusTone } from "../../theme/tokens";
import { bucketFor } from "../../utils/billBucket";
import type { BillBucket } from "../../utils/billBucket";

/** Colour tone of a status: ok (green), info (blue), warn (orange), err (red), neutral (grey). */
export type Tone = StatusTone;

/** Icon names understood by StatusChip (mapped to @mui/icons-material there). */
export type IconKey =
  | "schedule"
  | "send"
  | "check"
  | "error"
  | "pause"
  | "hourglass"
  | "warning"
  | "close"
  | "help"
  | "sync"
  | "block"
  | "link"
  | "link_off"
  | "info"
  | "review";

export type StatusMeta = { label: string; tone: Tone; icon: IconKey };

/** Lower-cases and drops spaces, underscores and hyphens: "Pending Payment" -> "pendingpayment". */
const normalize = (value?: string | null): string =>
  (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

/**
 * Bill status (Tipalti / QuickBooks pipeline), case-insensitive.
 * New or empty -> Not sent yet; PendingPayment -> Sent to Tipalti; Sent -> Sent;
 * Paid -> Paid; Failed/Error -> Failed; anything else Tipalti reports -> Deferred in Tipalti.
 */
export function billStatusMeta(status?: string | null): StatusMeta {
  switch (normalize(status)) {
    case "":
    case "new":
      return { label: "Not sent yet", tone: "neutral", icon: "schedule" };
    case "pendingpayment":
      return { label: "Sent to Tipalti", tone: "info", icon: "send" };
    case "sent":
      return { label: "Sent", tone: "info", icon: "send" };
    case "paid":
      return { label: "Paid", tone: "ok", icon: "check" };
    case "failed":
    case "error":
      return { label: "Failed", tone: "err", icon: "error" };
    default:
      return { label: "Deferred in Tipalti", tone: "warn", icon: "pause" };
  }
}

/**
 * The Status chip for a bill wherever one is listed (Dashboard grid, Bill detail, Bills
 * tables): billStatusMeta(status). A legacy bill with no bill status is shown by the bucket
 * its payment status puts it in (bucketFor), so a paid legacy bill never reads
 * "Not sent yet" and the chip always agrees with the counts and filters.
 */
export function billChipMeta(
  status?: string | null,
  paymentStatus?: string | null
): StatusMeta {
  if ((status ?? "").trim()) return billStatusMeta(status);
  const fallback: Record<BillBucket, () => StatusMeta> = {
    paid: () => billStatusMeta("Paid"),
    failed: () => billStatusMeta("Failed"),
    notSent: () => billStatusMeta("New"),
    sent: () => billStatusMeta("Sent"),
    other: () => paymentStatusMeta(paymentStatus),
  };
  return fallback[bucketFor(status, paymentStatus)]();
}

/**
 * Payment status, case-insensitive.
 * Unpaid or empty -> Unpaid; new/pending -> Pending; processed/paid/completed -> Processed;
 * failed/error -> Failed; anything else -> the raw text in a neutral chip.
 */
export function paymentStatusMeta(status?: string | null): StatusMeta {
  switch (normalize(status)) {
    case "":
    case "unpaid":
      return { label: "Unpaid", tone: "neutral", icon: "schedule" };
    case "new":
    case "pending":
      return { label: "Pending", tone: "info", icon: "hourglass" };
    case "processed":
    case "paid":
    case "completed":
      return { label: "Processed", tone: "ok", icon: "check" };
    case "failed":
    case "error":
      return { label: "Failed", tone: "err", icon: "error" };
    default:
      return { label: (status ?? "").trim(), tone: "neutral", icon: "help" };
  }
}

/**
 * Whether a payee can be paid by ACH. `true` or unknown (null/undefined) -> Payable;
 * `false` -> "Not ACH payable" plus the reason when one is given.
 */
export function payableMeta(
  payable?: boolean | null,
  reason?: string | null
): StatusMeta {
  if (payable === false) {
    const why = (reason ?? "").trim();
    return {
      label: `Not ACH payable${why ? ": " + why : ""}`,
      tone: "warn",
      icon: "warning",
    };
  }
  return { label: "Payable", tone: "ok", icon: "check" };
}

/** Optional wording for triMeta, e.g. { yes: "In Tipalti", no: "Not in Tipalti" }. */
export type TriLabels = { yes?: string; no?: string; unknown?: string };

/** Yes / No / Unknown for a nullable boolean (Tipalti check columns and filters). */
export function triMeta(v?: boolean | null, labels?: TriLabels): StatusMeta {
  if (v === true) {
    return { label: labels?.yes ?? "Yes", tone: "ok", icon: "check" };
  }
  if (v === false) {
    return { label: labels?.no ?? "No", tone: "err", icon: "close" };
  }
  return { label: labels?.unknown ?? "Unknown", tone: "neutral", icon: "help" };
}

/**
 * Maintenance job run state, case-insensitive: Running, Completed, Cancelled, Failed,
 * Interrupted. Anything else shows its raw text (or "Unknown") in a neutral chip.
 */
export function runStateMeta(state?: string | null): StatusMeta {
  switch (normalize(state)) {
    case "running":
      return { label: "Running", tone: "info", icon: "sync" };
    case "completed":
      return { label: "Completed", tone: "ok", icon: "check" };
    case "cancelled":
    case "canceled":
      return { label: "Cancelled", tone: "neutral", icon: "block" };
    case "failed":
      return { label: "Failed", tone: "err", icon: "error" };
    case "interrupted":
      return { label: "Interrupted", tone: "warn", icon: "pause" };
    default:
      return {
        label: (state ?? "").trim() || "Unknown",
        tone: "neutral",
        icon: "help",
      };
  }
}

/** QuickBooks company connection. */
export function connectionMeta(connected: boolean): StatusMeta {
  return connected
    ? { label: "Connected", tone: "ok", icon: "link" }
    : { label: "Not connected", tone: "neutral", icon: "link_off" };
}
